// src/components/Events/NewYear/NewYearEventPage.jsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import api from "../../../api/axios";

import useIsMobile from "./hooks/useIsMobile";
import useMatrixCanvas from "./hooks/useMatrixCanvas";
import useNewYearEvent from "./hooks/useNewYearEvent";
import useNewYearWS from "./hooks/useNewYearWS";

export default function NewYearEventPage() {
    const [text, setText] = useState("");
    const [toast, setToast] = useState(null);

    const isMobile = useIsMobile(768);

    const chatListRef = useRef(null);
    const canvasRef = useRef(null);
    const toastTimerRef = useRef(null);

    // ✅ 옵션 B: 채팅 루프 레인(무한) 타이머
    const rainTimerRef = useRef(null);

    // ✅ 매트릭스 캔버스 (모바일 최적화 옵션 포함)
    const { pushMatrixText } = useMatrixCanvas(canvasRef, { isMobile });

    const showToast = useCallback((msg) => {
        setToast(msg);
        if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
        toastTimerRef.current = setTimeout(() => setToast(null), 1500);
    }, []);

    const buildMediaUrl = useCallback((url) => {
        if (!url) return "";
        if (/^https?:\/\//i.test(url)) return url;

        const base = api?.defaults?.baseURL;
        if (base && /^https?:\/\//i.test(base)) {
            return `${base.replace(/\/$/, "")}${url.startsWith("/") ? "" : "/"}${url}`;
        }
        return `${window.location.origin}${url.startsWith("/") ? "" : "/"}${url}`;
    }, []);

    // ✅ 이벤트/메시지 로딩 (pushMatrixText를 여기서도 사용)
    const { event, loading, messages, setMessages } = useNewYearEvent(pushMatrixText);

    // ✅ WS 연결 (pushMatrixText를 WS 수신 시에도 사용)
    const { wsStatus, wsReady, sendWS, forceReconnect } = useNewYearWS({
        enabled: !loading && !!event,
        pushMatrixText,
        showToast,
        setMessages,
    });

    const canSend = wsReady && text.trim().length > 0;

    const send = useCallback(() => {
        const v = text.trim();
        if (!v) return;

        if (!sendWS({ text: v })) {
            showToast("연결이 끊겼어요. 재연결 중…");
            forceReconnect();
            return;
        }

        // ✅ 중복 낙하 방지:
        // - 내가 보낼 때 로컬에서 떨어뜨리지 말고
        // - WS 브로드캐스트로 돌아올 때만 떨어지게 한다
        // pushMatrixText(v);

        const localId = `local-${Date.now()}-${Math.random().toString(16).slice(2)}`;
        setMessages((prev) => [
            ...prev,
            { id: localId, content: v, display_name: "", created_at: "", _local: true },
        ]);

        setText("");
    }, [text, sendWS, showToast, forceReconnect, setMessages]);

    // ✅ 채팅 패널 자동 스크롤
    useEffect(() => {
        const el = chatListRef.current;
        if (!el) return;
        el.scrollTop = el.scrollHeight;
    }, [messages.length]);

    // ✅ 옵션 B: "기존 채팅 내용"으로 무한 레인
    useEffect(() => {
        if (!event || loading) return;

        if (rainTimerRef.current) {
            clearInterval(rainTimerRef.current);
            rainTimerRef.current = null;
        }

        const getPool = () =>
            messages
                .slice(-60)
                .map((m) => (m?.content ?? "").trim())
                .filter(Boolean);

        // ✅ 모바일은 렉 줄이기 위해 interval을 더 늘림
        const intervalMs = isMobile ? 650 : 140;

        rainTimerRef.current = setInterval(() => {
            const pool = getPool();
            if (pool.length === 0) return;
            const pick =
                pool.length > 0
                    ? pool[Math.floor(Math.random() * pool.length)]
                    : "HAPPY NEW YEAR";

            pushMatrixText(pick, {
                source: "loop",
                speed: (isMobile ? 1.1 : 1.6) + Math.random() * (isMobile ? 1.2 : 1.7),
                fontSize: (isMobile ? 14 : 18) + Math.random() * (isMobile ? 7 : 10),
                opacity: 0.08 + Math.random() * 0.12,
            });
        }, intervalMs);

        return () => {
            if (rainTimerRef.current) {
                clearInterval(rainTimerRef.current);
                rainTimerRef.current = null;
            }
        };
    }, [event, loading, messages, pushMatrixText, isMobile]);

    // ✅ 공통 cleanup
    useEffect(() => {
        return () => {
            if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
            if (rainTimerRef.current) clearInterval(rainTimerRef.current);
            rainTimerRef.current = null;
        };
    }, []);

    if (loading) {
        return (
            <div style={{ padding: 24 }}>
                <h3>신년 이벤트 불러오는 중...</h3>
            </div>
        );
    }

    if (!event) {
        return (
            <div style={{ padding: 24 }}>
                <h3>신년 이벤트가 진행 중이 아닙니다.</h3>
            </div>
        );
    }

    const centerSrc = buildMediaUrl(event.center_image);
    const inputWrapMaxWidth = isMobile ? "100%" : 980;

    const stageH = isMobile ? "min(82vh, calc(100dvh - 120px))" : "70vh";

    const stageBoxStyle = {
        position: "relative",

        // ✅ 모바일은 높이를 먼저 꽉 채움 (입력창 제외)
        height: isMobile ? stageH : undefined,
        maxHeight: stageH,

        // ✅ 그 높이에 맞춰 4:5 폭 계산 + 너무 넓어지면 94vw로 제한
        width: isMobile ? `min(94vw, calc(${stageH} * 4 / 5))` : "min(72vw, 1100px)",

        aspectRatio: isMobile ? "4 / 5" : "16 / 9",
    };

    return (
        <div style={{ position: "fixed", inset: 0, overflow: "hidden", background: "#050510", zIndex: 1000, isolation: "isolate" }}>
            {/* center stage */}
            <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10, pointerEvents: "none" }}>
                <div style={stageBoxStyle}>
                    {centerSrc ? (
                        <>
                            <img
                                src={centerSrc}
                                alt="newyear"
                                style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                    display: "block",
                                    filter: isMobile ? "none" : "drop-shadow(0 0 30px rgba(255,215,0,0.25))",
                                    position: "relative",
                                    zIndex: 1,
                                }}
                            />

                            {/* ✅ 가독성용 오버레이 (이미지 위 / 캔버스 아래) */}
                            <div
                                style={{
                                    position: "absolute",
                                    inset: 0,
                                    zIndex: 1.5, // img(1) < overlay(1.5) < canvas(2)
                                    pointerEvents: "none",
                                    background:
                                        "radial-gradient(circle at 50% 45%, rgba(0,0,0,0.10) 0%, rgba(0,0,0,0.30) 55%, rgba(0,0,0,0.48) 100%)," +
                                        "linear-gradient(to bottom, rgba(0,0,0,0.10), rgba(0,0,0,0.38))",
                                    mixBlendMode: "multiply",
                                }}
                            />
                        </>
                    ) : (
                        <div style={{ color: "white", opacity: 0.7 }}>
                            center_image가 설정되지 않았습니다
                        </div>
                    )}

                    <canvas ref={canvasRef} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", zIndex: 2, pointerEvents: "none" }} />
                </div>
            </div>

            {/* chat panel */}
            {!isMobile && (
                <div style={{ position: "absolute", top: 16, right: 24, zIndex: 20, width: "clamp(300px, 28vw, 420px)", maxWidth: "calc(100vw - 48px)", height: "58vh", borderRadius: 16, overflow: "hidden", boxSizing: "border-box", border: "1px solid rgba(255,255,255,0.12)", background: "rgba(0,0,0,0.35)", backdropFilter: "blur(10px)", display: "flex", flexDirection: "column", pointerEvents: "auto" }}>
                    <div style={{ padding: "10px 12px", borderBottom: "1px solid rgba(255,255,255,0.10)", color: "white", opacity: 0.92, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                        <span>신년 이벤트 채팅</span>
                        <span style={{ fontSize: 12, opacity: 0.7 }}>WS: {wsStatus}</span>
                    </div>

                    <div ref={chatListRef} style={{ flex: 1, overflowY: "auto", padding: 10 }}>
                        {messages.length === 0 ? (
                            <div style={{ color: "white", opacity: 0.6, fontSize: 13 }}>아직 메시지가 없어요</div>
                        ) : (
                            messages.slice(-200).map((m) => (
                                <div key={m.id} style={{ marginBottom: 8 }}>
                                    {m.display_name && (
                                        <div style={{ color: "rgba(255,255,255,0.75)", fontSize: 12 }}>
                                            {m.display_name}
                                            <span style={{ opacity: 0.5 }}>{m.created_at ? ` · ${m.created_at}` : ""}</span>
                                        </div>
                                    )}
                                    <div style={{ color: "white", fontSize: 13, wordBreak: "break-word" }}>{m.content}</div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}

            {/* toast */}
            {toast && (
                <div style={{ position: "absolute", left: "50%", bottom: 84, transform: "translateX(-50%)", zIndex: 20, padding: "10px 14px", borderRadius: 999, background: "rgba(0,0,0,0.55)", border: "1px solid rgba(255,255,255,0.15)", color: "white", backdropFilter: "blur(6px)", whiteSpace: "nowrap", pointerEvents: "none" }}>
                    {toast}
                </div>
            )}

            {/* input */}
            <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, zIndex: 10, padding: isMobile ? 12 : 16, display: "flex", justifyContent: "center", pointerEvents: "auto" }}>
                <div style={{ width: "100%", maxWidth: inputWrapMaxWidth, display: "flex", gap: 10, alignItems: "center", background: "rgba(0,0,0,0.25)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 16, padding: isMobile ? 10 : 12, backdropFilter: "blur(8px)", boxSizing: "border-box" }}>
                    {isMobile ? (
                        <input
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") send(); }}
                            placeholder={`채팅 치면 +${event.reward_leaf} Leaf! (WS: ${wsStatus})`}
                            style={{ flex: "1 1 auto", minWidth: 0, height: 48, padding: "0 14px", lineHeight: "48px", boxSizing: "border-box", borderRadius: 12, border: "1px solid rgba(255,255,255,0.15)", background: "rgba(0,0,0,0.35)", color: "white", outline: "none", display: "block" }}
                        />
                    ) : (
                        <textarea
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                            rows={2}
                            placeholder={`채팅 치면 +${event.reward_leaf} Leaf! (Enter=전송 / Shift+Enter=줄바꿈) (WS: ${wsStatus})`}
                            style={{ flex: "1 1 auto", minWidth: 0, resize: "none", padding: "12px 14px", borderRadius: 12, border: "1px solid rgba(255,255,255,0.15)", background: "rgba(0,0,0,0.35)", color: "white", outline: "none", lineHeight: 1.45, fontSize: 15, minHeight: 64, boxSizing: "border-box", display: "block" }}
                        />
                    )}

                    <button
                        type="button"
                        onClick={send}
                        disabled={!canSend}
                        style={{ flex: "0 0 auto", width: "auto", maxWidth: "none", minWidth: 64, height: isMobile ? 48 : 44, padding: isMobile ? "0 14px" : "0 12px", borderRadius: 12, border: "1px solid rgba(255,255,255,0.15)", background: "rgba(255,215,0,0.15)", color: "white", cursor: canSend ? "pointer" : "not-allowed", opacity: canSend ? 1 : 0.55, fontSize: isMobile ? 14 : 13, whiteSpace: "nowrap", userSelect: "none", boxSizing: "border-box", display: "inline-flex", alignItems: "center", justifyContent: "center" }}
                        title={!wsReady ? "웹소켓 재연결 중" : text.trim() ? "전송" : "내용을 입력하세요"}
                    >
                        전송
                    </button>
                </div>
            </div>
        </div>
    );
}
